import { useStockLocation } from './stocklocations'
import { useCrossdockingSuppliers } from './suppliers'
import { CASARRUBUELOS } from './stocklocations'
import { useDeliveryUtils } from './deliveryutils'
import { useGeozone } from './geozone'
import { useShippingMethods } from './shippingmethods'

const { FAMILY_HOME_DELIVERY_24H } = useShippingMethods()
// Constantes
const SOURCE_NUTRITIENDA = 0
const NUTRITIENDA_CUTOFF_TIME = 17
const DEFAULT_SHIPPING_METHOD_ID = FAMILY_HOME_DELIVERY_24H
const FREE_SHIPPING_THRESHOLD = 25

// Tipos
type DeliveryTimeCalculatorResult = {
  delivery_time: Date
  delivery_text_id: string | undefined
  delivery_text_params: Record<string, any>
  expires_at_seconds: number
  delivery_location_text_id?: string | null
  delivery_location_text_params?: Record<string, any> | null
  debug_info: Record<string, any>
}

const { GEO_ZONE_PENINSULA, getOffset } = useGeozone()
const { getSupplierEta, getSupplierLimitHour } = useCrossdockingSuppliers()
const { getForGeozone } = useStockLocation()
const {
  getNextWorkDay,
  getDeliveryLimit,
  getNextCanaryDelivery,
  getLimitHour,
} = useDeliveryUtils()

// Métodos privados
const calculateCrossdocking = (
  source: number,
  geozone_id: number
): DeliveryTimeCalculatorResult => {
  let date = new Date()
  date = getSupplierEta(source, date)
  const stock_location_id = CASARRUBUELOS

  const result = _calculate(date, stock_location_id, geozone_id)

  // Overwrite valid until if needed
  const valid_until = new Date()
  const current_hour = valid_until.getHours()
  const cutoff_hour = getSupplierLimitHour(source)

  if (current_hour >= cutoff_hour) {
    valid_until.setDate(valid_until.getDate() + 1)
  }

  getNextWorkDay(valid_until)
  valid_until.setHours(cutoff_hour, 0)

  const expires_at_seconds =
    (valid_until.getTime() - new Date().getTime()) / 1000

  result.expires_at_seconds = expires_at_seconds
  result.delivery_text_params = { '{time}': cutoff_hour }

  return result
}

const calculateRegular = (
  stock_location_id: number,
  geozone_id: number
): DeliveryTimeCalculatorResult => {
  const date = new Date()
  return _calculate(date, stock_location_id, geozone_id)
}

const _calculate = (
  date: Date,
  stock_location_id: number,
  geozone_id: number
): DeliveryTimeCalculatorResult => {
  const offset = getOffset(geozone_id)

  if (offset) {
    date.setHours(applyOperation(date.getHours(), offset))
  }

  const confirmed = new Date(date)

  const gz_stock_location_id = getForGeozone(geozone_id)
  let delivery_time, delivery_location_text_id, delivery_location_text_params

  if (stock_location_id === gz_stock_location_id) {
    delivery_time = getDeliveryLimit(confirmed, geozone_id)
  } else {
    delivery_time = getNextCanaryDelivery(confirmed)
    delivery_location_text_id = 'product_est_delivery_backup_sl'
    delivery_location_text_params = { '{location}': 'Peninsula' }
  }

  const cutoff_hour = getLimitHour(confirmed, geozone_id)

  const valid_until = new Date()
  const current_hour = valid_until.getHours()
  let delivery_text_id

  if (current_hour < cutoff_hour) {
    delivery_text_id = 'product_est_delivery_limit_today'
  } else {
    valid_until.setDate(valid_until.getDate() + 1)
  }

  getNextWorkDay(valid_until)

  const same_day = valid_until.getDate() === new Date().getDate()
  const master_cutoff_hour = getLimitHour(new Date(), GEO_ZONE_PENINSULA)

  if (same_day) {
    valid_until.setHours(master_cutoff_hour, 0)
  } else {
    valid_until.setHours(0, 0)
  }

  const expires_at_seconds =
    (valid_until.getTime() - new Date().getTime()) / 1000

  return {
    delivery_time,
    delivery_text_id,
    delivery_text_params: { '{time}': cutoff_hour },
    expires_at_seconds,
    delivery_location_text_id,
    delivery_location_text_params,
    debug_info: {
      dbg_now: new Date().toUTCString(),
      dbg_date: date.toUTCString(),
      dbg_current_hour: current_hour,
      dbg_cutoff_hour: cutoff_hour,
      dbg_confirmed: confirmed.toUTCString(),
      dbg_valid_until: valid_until.toUTCString(),
      dbg_sameday: same_day,
      dbg_master_cutoff_hour: master_cutoff_hour,
    },
  }
}

// Composable principal
export const useETACalculator = () => {
  const calculate = (
    source: number,
    stock_location_id: number,
    geozone_id: number,
    locale: string = 'es'
  ) => {
    let data
    if (source > 0) {
      data = calculateCrossdocking(source, geozone_id)
    } else {
      data = calculateRegular(stock_location_id, geozone_id)
    }

    const text = formatText(data, locale)
    return text
  }

  return {
    calculate,
  }
}

const applyOperation = (value: number, operation: string): number => {
  const operator = operation[0]
  const amount = parseFloat(operation.slice(1))

  switch (operator) {
    case '+':
      return value + amount
    case '-':
      return value - amount
    default:
      throw new Error('Unsupported operation')
  }
}

const TEXT_TRANSLATIONS: {
  [key: string]: Record<string, string>
} = {
  es: {
    product_est_delivery:
      'Entrega {{delivery_info}} entre el <span style="font-weight: bold;">{date}</span>',
    product_est_delivery_delayed:
      '&#9888; Debido a la situación meteorológica actual, la entrega {{delivery_info}} es entre el <span style="font-weight: bold;">{date}</span>.',
    product_est_delivery_limit_today: 'Haz tu pedido en {{countdown}}',
    product_est_delivery_limit_tomorrow:
      'Haz tu pedido antes de mañana a las {{countdown}}',
    product_est_delivery_backup_sl: 'Envío desde Almacén Península',
  },
  pt: {
    product_est_delivery:
      'Entrega {{delivery_info}} entre o <span style="font-weight: bold;">{date}</span>',
    product_est_delivery_delayed:
      '&#9888; Devido à situação meteorológica atual, a entrega {{delivery_info}} é entre o <span style="font-weight: bold;">{date}</span>.',
    product_est_delivery_limit_today: 'Faça o seu pedido em {{countdown}}',
    product_est_delivery_limit_tomorrow:
      'Faça o seu pedido antes de amanhã às {{countdown}}',
    product_est_delivery_backup_sl: 'Envio desde Armazém Península',
  },
  it: {
    product_est_delivery:
      'Consegna {{delivery_info}} tra il <span style="font-weight: bold;">{date}</span>',
    product_est_delivery_delayed:
      '&#9888; A causa delle condizioni meteorologiche attuali, la consegna {{delivery_info}} è tra il <span style="font-weight: bold;">{date}</span>.',
    product_est_delivery_limit_today: 'Fai il tuo ordine entro {{countdown}}',
    product_est_delivery_limit_tomorrow:
      'Fai il tuo ordine entro domani alle {{countdown}}',
    product_est_delivery_backup_sl: 'Spedizione da Magazzino Penisola',
  },
  de: {
    product_est_delivery:
      'Lieferung {{delivery_info}} zwischen dem <span style="font-weight: bold;">{date}</span>',
    product_est_delivery_delayed:
      '&#9888; Aufgrund der aktuellen Wetterbedingungen erfolgt die Lieferung {{delivery_info}} zwischen dem <span style="font-weight: bold;">{date}</span>.',
    product_est_delivery_limit_today: 'Bestellen Sie vor {{countdown}}',
    product_est_delivery_limit_tomorrow:
      'Bestellen Sie vor morgen um {{countdown}}',
    product_est_delivery_backup_sl: 'Versand ab Lager Halbinsel',
  },
  fr: {
    product_est_delivery:
      'Livraison {{delivery_info}} entre le <span style="font-weight: bold;">{date}</span>',
    product_est_delivery_delayed:
      '&#9888; En raison des conditions météorologiques actuelles, la livraison {{delivery_info}} est entre le <span style="font-weight: bold;">{date}</span>.',
    product_est_delivery_limit_today:
      'Passez votre commande avant {{countdown}}',
    product_est_delivery_limit_tomorrow:
      'Passez votre commande avant demain à {{countdown}}',
    product_est_delivery_backup_sl: "Expédition depuis l'entrepôt Péninsule",
  },
  be: {
    product_est_delivery:
      'Livraison {{delivery_info}} entre le <span style="font-weight: bold;">{date}</span>',
    product_est_delivery_delayed:
      '&#9888; En raison des conditions météorologiques actuelles, la livraison {{delivery_info}} est entre le <span style="font-weight: bold;">{date}</span>.',
    product_est_delivery_limit_today:
      'Passez votre commande avant {{countdown}}',
    product_est_delivery_limit_tomorrow:
      'Passez votre commande avant demain à {{countdown}}',
    product_est_delivery_backup_sl: "Expédition depuis l'entrepôt Péninsule",
  },
  ch: {
    product_est_delivery:
      'Livraison {{delivery_info}} entre le <span style="font-weight: bold;">{date}</span>',
    product_est_delivery_delayed:
      '&#9888; En raison des conditions météorologiques actuelles, la livraison {{delivery_info}} est entre le <span style="font-weight: bold;">{date}</span>.',
    product_est_delivery_limit_today:
      'Passez votre commande avant {{countdown}}',
    product_est_delivery_limit_tomorrow:
      'Passez votre commande avant demain à {{countdown}}',
    product_est_delivery_backup_sl: "Expédition depuis l'entrepôt Péninsule",
  },
  at: {
    product_est_delivery:
      'Lieferung {{delivery_info}} zwischen dem <span style="font-weight: bold;">{date}</span>',
    product_est_delivery_delayed:
      '&#9888; Aufgrund der aktuellen Wetterbedingungen erfolgt die Lieferung {{delivery_info}} zwischen dem <span style="font-weight: bold;">{date}</span>.',
    product_est_delivery_limit_today: 'Bestellen Sie vor {{countdown}}',
    product_est_delivery_limit_tomorrow:
      'Bestellen Sie vor morgen um {{countdown}}',
    product_est_delivery_backup_sl: 'Versand ab Lager Halbinsel',
  },
  nl: {
    product_est_delivery:
      'Levering {{delivery_info}} tussen <span style="font-weight: bold;">{date}</span>',
    product_est_delivery_delayed:
      '&#9888; Vanwege de huidige weersomstandigheden is de levering {{delivery_info}} tussen <span style="font-weight: bold;">{date}</span>.',
    product_est_delivery_limit_today: 'Bestel voor {{countdown}}',
    product_est_delivery_limit_tomorrow: 'Bestel voor morgen om {{countdown}}',
    product_est_delivery_backup_sl: 'Verzending vanaf het schiereilandmagazijn',
  },
  hu: {
    product_est_delivery:
      'Szállítás {{delivery_info}} között <span style="font-weight: bold;">{date}</span>',
    product_est_delivery_delayed:
      '&#9888; A jelenlegi időjárási helyzet miatt a szállítás {{delivery_info}} között <span style="font-weight: bold;">{date}</span>.',
    product_est_delivery_limit_today: 'Rendeljen {{countdown}} előtt',
    product_est_delivery_limit_tomorrow: 'Rendeljen holnap előtt {{countdown}}',
    product_est_delivery_backup_sl: 'Szállítás a félszigeti raktárból',
  },
  cz: {
    product_est_delivery:
      'Doručení {{delivery_info}} mezi <span style="font-weight: bold;">{date}</span>',
    product_est_delivery_delayed:
      '&#9888; Vzhledem k současné povětrnostní situaci je doručení {{delivery_info}} mezi <span style="font-weight: bold;">{date}</span>.',
    product_est_delivery_limit_today: 'Objednejte před {{countdown}}',
    product_est_delivery_limit_tomorrow:
      'Objednejte před zítřkem {{countdown}}',
    product_est_delivery_backup_sl: 'Odeslání ze skladu na poloostrově',
  },
  pl: {
    product_est_delivery:
      'Dostawa {{delivery_info}} między <span style="font-weight: bold;">{date}</span>',
    product_est_delivery_delayed:
      '&#9888; Ze względu na aktualne warunki pogodowe dostawa {{delivery_info}} jest między <span style="font-weight: bold;">{date}</span>.',
    product_est_delivery_limit_today: 'Zamów przed {{countdown}}',
    product_est_delivery_limit_tomorrow: 'Zamów przed jutrem {{countdown}}',
    product_est_delivery_backup_sl: 'Wysyłka z magazynu na półwyspie',
  },
  dk: {
    product_est_delivery:
      'Levering {{delivery_info}} mellem <span style="font-weight: bold;">{date}</span>',
    product_est_delivery_delayed:
      '&#9888; På grund af de nuværende vejrforhold er leveringen {{delivery_info}} mellem <span style="font-weight: bold;">{date}</span>.',
    product_est_delivery_limit_today: 'Bestil før {{countdown}}',
    product_est_delivery_limit_tomorrow: 'Bestil før i morgen {{countdown}}',
    product_est_delivery_backup_sl: 'Forsendelse fra halvøens lager',
  },
  fi: {
    product_est_delivery:
      'Toimitus {{delivery_info}} välillä <span style="font-weight: bold;">{date}</span>',
    product_est_delivery_delayed:
      '&#9888; Nykyisten sääolosuhteiden vuoksi toimitus {{delivery_info}} on välillä <span style="font-weight: bold;">{date}</span>.',
    product_est_delivery_limit_today: 'Tilaa ennen {{countdown}}',
    product_est_delivery_limit_tomorrow: 'Tilaa ennen huomista {{countdown}}',
    product_est_delivery_backup_sl: 'Lähetys niemimaan varastosta',
  },
  gr: {
    product_est_delivery:
      'Παράδοση {{delivery_info}} μεταξύ <span style="font-weight: bold;">{date}</span>',
    product_est_delivery_delayed:
      '&#9888; Λόγω των τρεχουσών καιρικών συνθηκών, η παράδοση {{delivery_info}} είναι μεταξύ <span style="font-weight: bold;">{date}</span>.',
    product_est_delivery_limit_today: 'Παραγγείλετε πριν από {{countdown}}',
    product_est_delivery_limit_tomorrow:
      'Παραγγείλετε πριν από αύριο {{countdown}}',
    product_est_delivery_backup_sl: 'Αποστολή από την αποθήκη της χερσονήσου',
  },
  no: {
    product_est_delivery:
      'Levering {{delivery_info}} mellom <span style="font-weight: bold;">{date}</span>',
    product_est_delivery_delayed:
      '&#9888; På grunn av de nåværende værforholdene er leveringen {{delivery_info}} mellom <span style="font-weight: bold;">{date}</span>.',
    product_est_delivery_limit_today: 'Bestill før {{countdown}}',
    product_est_delivery_limit_tomorrow: 'Bestill før i morgen {{countdown}}',
    product_est_delivery_backup_sl: 'Forsendelse fra halvøyens lager',
  },
  ro: {
    product_est_delivery:
      'Livrare {{delivery_info}} între <span style="font-weight: bold;">{date}</span>',
    product_est_delivery_delayed:
      '&#9888; Din cauza condițiilor meteorologice actuale, livrarea {{delivery_info}} este între <span style="font-weight: bold;">{date}</span>.',
    product_est_delivery_limit_today: 'Comandați înainte de {{countdown}}',
    product_est_delivery_limit_tomorrow:
      'Comandați înainte de mâine {{countdown}}',
    product_est_delivery_backup_sl: 'Expediere din depozitul peninsulei',
  },
  sk: {
    product_est_delivery:
      'Doručenie {{delivery_info}} medzi <span style="font-weight: bold;">{date}</span>',
    product_est_delivery_delayed:
      '&#9888; Vzhľadom na súčasné poveternostné podmienky je doručenie {{delivery_info}} medzi <span style="font-weight: bold;">{date}</span>.',
    product_est_delivery_limit_today: 'Objednajte pred {{countdown}}',
    product_est_delivery_limit_tomorrow:
      'Objednajte pred zajtrajškom {{countdown}}',
    product_est_delivery_backup_sl: 'Odoslanie zo skladu na polostrove',
  },
  se: {
    product_est_delivery:
      'Leverans {{delivery_info}} mellan <span style="font-weight: bold;">{date}</span>',
    product_est_delivery_delayed:
      '&#9888; På grund av de nuvarande väderförhållandena är leveransen {{delivery_info}} mellan <span style="font-weight: bold;">{date}</span>.',
    product_est_delivery_limit_today: 'Beställ före {{countdown}}',
    product_est_delivery_limit_tomorrow: 'Beställ före imorgon {{countdown}}',
    product_est_delivery_backup_sl: 'Frakt från halvöns lager',
  },
  tr: {
    product_est_delivery:
      'Teslimat {{delivery_info}} arasında <span style="font-weight: bold;">{date}</span>',
    product_est_delivery_delayed:
      '&#9888; Mevcut hava koşulları nedeniyle teslimat {{delivery_info}} arasında <span style="font-weight: bold;">{date}</span>.',
    product_est_delivery_limit_today: '{{countdown}} öncesinde sipariş verin',
    product_est_delivery_limit_tomorrow:
      'Yarın öncesinde sipariş verin {{countdown}}',
    product_est_delivery_backup_sl: 'Yarımada deposundan gönderim',
  },
  si: {
    product_est_delivery:
      'Dostava {{delivery_info}} med <span style="font-weight: bold;">{date}</span>',
    product_est_delivery_delayed:
      '&#9888; Zaradi trenutnih vremenskih razmer je dostava {{delivery_info}} med <span style="font-weight: bold;">{date}</span>.',
    product_est_delivery_limit_today: 'Naročite pred {{countdown}}',
    product_est_delivery_limit_tomorrow: 'Naročite pred jutri {{countdown}}',
    product_est_delivery_backup_sl: 'Pošiljanje iz skladišča na polotoku',
  },
}

const MONTHS = {
  es: {
    0: 'enero',
    1: 'febrero',
    2: 'marzo',
    3: 'abril',
    4: 'mayo',
    5: 'junio',
    6: 'julio',
    7: 'agosto',
    8: 'septiembre',
    9: 'octubre',
    10: 'noviembre',
    11: 'diciembre',
  },
  pt: {
    0: 'janeiro',
    1: 'fevereiro',
    2: 'março',
    3: 'abril',
    4: 'maio',
    5: 'junho',
    6: 'julho',
    7: 'agosto',
    8: 'setembro',
    9: 'outubro',
    10: 'novembro',
    11: 'dezembro',
  },
  fr: {
    0: 'janvier',
    1: 'février',
    2: 'mars',
    3: 'avril',
    4: 'mai',
    5: 'juin',
    6: 'juillet',
    7: 'août',
    8: 'septembre',
    9: 'octobre',
    10: 'novembre',
    11: 'décembre',
  },
  be: {
    0: 'janvier',
    1: 'février',
    2: 'mars',
    3: 'avril',
    4: 'mai',
    5: 'juin',
    6: 'juillet',
    7: 'août',
    8: 'septembre',
    9: 'octobre',
    10: 'novembre',
    11: 'décembre',
  },
  ch: {
    0: 'janvier',
    1: 'février',
    2: 'mars',
    3: 'avril',
    4: 'mai',
    5: 'juin',
    6: 'juillet',
    7: 'août',
    8: 'septembre',
    9: 'octobre',
    10: 'novembre',
    11: 'décembre',
  },
  it: {
    0: 'gennaio',
    1: 'febbraio',
    2: 'marzo',
    3: 'aprile',
    4: 'maggio',
    5: 'giugno',
    6: 'luglio',
    7: 'agosto',
    8: 'settembre',
    9: 'ottobre',
    10: 'novembre',
    11: 'dicembre',
  },
  de: {
    0: 'Januar',
    1: 'Februar',
    2: 'März',
    3: 'April',
    4: 'Mai',
    5: 'Juni',
    6: 'Juli',
    7: 'August',
    8: 'September',
    9: 'Oktober',
    10: 'November',
    11: 'Dezember',
  },
  at: {
    0: 'Januar',
    1: 'Februar',
    2: 'März',
    3: 'April',
    4: 'Mai',
    5: 'Juni',
    6: 'Juli',
    7: 'August',
    8: 'September',
    9: 'Oktober',
    10: 'November',
    11: 'Dezember',
  },
  nl: {
    0: 'januari',
    1: 'februari',
    2: 'maart',
    3: 'april',
    4: 'mei',
    5: 'juni',
    6: 'juli',
    7: 'augustus',
    8: 'september',
    9: 'oktober',
    10: 'november',
    11: 'december',
  },
  hu: {
    0: 'január',
    1: 'február',
    2: 'március',
    3: 'április',
    4: 'május',
    5: 'június',
    6: 'július',
    7: 'augusztus',
    8: 'szeptember',
    9: 'október',
    10: 'november',
    11: 'december',
  },
  cz: {
    0: 'leden',
    1: 'únor',
    2: 'březen',
    3: 'duben',
    4: 'květen',
    5: 'červen',
    6: 'červenec',
    7: 'srpen',
    8: 'září',
    9: 'říjen',
    10: 'listopad',
    11: 'prosinec',
  },
  pl: {
    0: 'styczeń',
    1: 'luty',
    2: 'marzec',
    3: 'kwiecień',
    4: 'maj',
    5: 'czerwiec',
    6: 'lipiec',
    7: 'sierpień',
    8: 'wrzesień',
    9: 'październik',
    10: 'listopad',
    11: 'grudzień',
  },
  dk: {
    0: 'januar',
    1: 'februar',
    2: 'marts',
    3: 'april',
    4: 'maj',
    5: 'juni',
    6: 'juli',
    7: 'august',
    8: 'september',
    9: 'oktober',
    10: 'november',
    11: 'december',
  },
  fi: {
    0: 'tammikuu',
    1: 'helmikuu',
    2: 'maaliskuu',
    3: 'huhtikuu',
    4: 'toukokuu',
    5: 'kesäkuu',
    6: 'heinäkuu',
    7: 'elokuu',
    8: 'syyskuu',
    9: 'lokakuu',
    10: 'marraskuu',
    11: 'joulukuu',
  },
  gr: {
    0: 'Ιανουάριος',
    1: 'Φεβρουάριος',
    2: 'Μάρτιος',
    3: 'Απρίλιος',
    4: 'Μάιος',
    5: 'Ιούνιος',
    6: 'Ιούλιος',
    7: 'Αύγουστος',
    8: 'Σεπτέμβριος',
    9: 'Οκτώβριος',
    10: 'Νοέμβριος',
    11: 'Δεκέμβριος',
  },
  no: {
    0: 'januar',
    1: 'februar',
    2: 'mars',
    3: 'april',
    4: 'mai',
    5: 'juni',
    6: 'juli',
    7: 'august',
    8: 'september',
    9: 'oktober',
    10: 'november',
    11: 'desember',
  },
  ro: {
    0: 'ianuarie',
    1: 'februarie',
    2: 'martie',
    3: 'aprilie',
    4: 'mai',
    5: 'iunie',
    6: 'iulie',
    7: 'august',
    8: 'septembrie',
    9: 'octombrie',
    10: 'noiembrie',
    11: 'decembrie',
  },
  sk: {
    0: 'január',
    1: 'február',
    2: 'marec',
    3: 'apríl',
    4: 'máj',
    5: 'jún',
    6: 'júl',
    7: 'august',
    8: 'september',
    9: 'október',
    10: 'november',
    11: 'december',
  },
  se: {
    0: 'januari',
    1: 'februari',
    2: 'mars',
    3: 'april',
    4: 'maj',
    5: 'juni',
    6: 'juli',
    7: 'augusti',
    8: 'september',
    9: 'oktober',
    10: 'november',
    11: 'december',
  },
  tr: {
    0: 'ocak',
    1: 'şubat',
    2: 'mart',
    3: 'nisan',
    4: 'mayıs',
    5: 'haziran',
    6: 'temmuz',
    7: 'ağustos',
    8: 'eylül',
    9: 'ekim',
    10: 'kasım',
    11: 'aralık',
  },
  si: {
    0: 'januar',
    1: 'februar',
    2: 'marec',
    3: 'april',
    4: 'maj',
    5: 'junij',
    6: 'julij',
    7: 'avgust',
    8: 'september',
    9: 'oktober',
    10: 'november',
    11: 'december',
  },
}

const formatText = (
  etaCalculated: DeliveryTimeCalculatorResult,

  locale: string
) => {
  const delivery_text_id = etaCalculated?.delivery_text_id
  const delivery_text_params = etaCalculated?.delivery_text_params
  const delivery_location_text_id = etaCalculated?.delivery_location_text_id
  const delivery_location_text_params =
    etaCalculated?.delivery_location_text_params

  let text = ''

  let delivery_text = TEXT_TRANSLATIONS[locale]['product_est_delivery']
  const delivery_time = etaCalculated?.delivery_time
  const delivery_time_date = new Date(delivery_time)

  const format_delivery_date = delivery_time_date.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'long',
  })

  const next_day = new Date(delivery_time)
  next_day.setDate(next_day.getDate() + 1)
  getNextWorkDay(next_day)

  const nextDay = next_day.getDate()

  const finalDate = `${delivery_time_date.getDate()} - ${nextDay}  ${format_delivery_date.replace(`${delivery_time_date.getDate()}`, '')}`

  text = `${delivery_text.replace('{date}', `${finalDate}`)}. `

  if (delivery_text_id) {
    let delivery_text = TEXT_TRANSLATIONS[locale][delivery_text_id]
    for (const key in delivery_text_params) {
      delivery_text = delivery_text.replace(key, delivery_text_params[key])
    }
    text += `${delivery_text}.`
  }

  return {
    text,
    cutt_off: delivery_text_params['{time}'],
    last_date: next_day.toISOString(),
    checkout_text: finalDate,
  }
}
